import { Injectable } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from 'projects/webapp/app/environments/environment';

@Injectable({ providedIn: 'root' })
export class GooglePlacesService {
  private _loaderPromise?: Promise<typeof google>;
  private _geocoder?: google.maps.Geocoder;

  async ensureLoaded(language = 'de', region = 'CH'): Promise<boolean> {
    if (!environment.googleMapsApiKey) return false;
    if (!this._loaderPromise) {
      const loader = new Loader({
        apiKey: environment.googleMapsApiKey,
        libraries: ['places'],
        language,
        region,
      });
      this._loaderPromise = loader.load();
    }
    await this._loaderPromise;
    if (!this._geocoder) {
      this._geocoder = new google.maps.Geocoder();
    }
    return true;
  }

  async getPredictions(
    request: google.maps.places.AutocompleteRequest,
  ): Promise<google.maps.places.PlacePrediction[]> {
    const ok = await this.ensureLoaded();
    if (!ok) return [];
    try {
      const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      return suggestions
        .map(s => s.placePrediction)
        .filter((p): p is google.maps.places.PlacePrediction => !!p);
    } catch {
      return [];
    }
  }

  async getPlaceDetails(
    request: google.maps.places.PlaceDetailsRequest,
  ): Promise<google.maps.places.Place | null> {
    const ok = await this.ensureLoaded();
    if (!ok || !request.placeId) return null;
    try {
      const place = new google.maps.places.Place({
        id: request.placeId,
        requestedLanguage: request.language,
        requestedRegion: request.region,
      });
      await place.fetchFields({ fields: request.fields ?? [] });
      return place;
    } catch {
      return null;
    }
  }

  async getGeocoderDetails(
    request: google.maps.GeocoderRequest,
  ): Promise<google.maps.GeocoderResult[] | null> {
    const ok = await this.ensureLoaded();
    if (!ok) return null;
    return new Promise((resolve) => {
      this._geocoder!.geocode(request, (results, status) => {
        if (status === 'OK' && results) {
          resolve(results);
        } else {
          resolve(null);
        }
      });
    })
  }

  async newSessionToken(): Promise<google.maps.places.AutocompleteSessionToken | undefined> {
    const ok = await this.ensureLoaded();
    if (!ok) return undefined;
    return new google.maps.places.AutocompleteSessionToken();
  }

  async hasAddressInCity(address: string, locality: string, country = 'ch'): Promise<boolean> {
    const predictions = await this.getPredictions({
      input: `${address} ${locality}`,
      includedRegionCodes: [country],
      includedPrimaryTypes: ['address'],
    });
    if (predictions.length === 0) return false;
    const localityLower = locality.toLowerCase();
    return predictions.some(p => p.secondaryText?.text.toLowerCase().includes(localityLower));
  }

  async hasCityPostalCode(postalCode: string, locality: string, country = 'ch'): Promise<boolean> {
    const predictions = await this.getPredictions({
      input: `${postalCode} ${locality}`,
      includedRegionCodes: [country],
      includedPrimaryTypes: ['address'],
    });
    if (predictions.length === 0) return false;
    const localityLower = locality.toLowerCase();
    return predictions.some(p => p.secondaryText?.text.toLowerCase().includes(localityLower));
  }
}
